import { useCallback } from 'react';
import { useStoreon } from 'storeon/react';
import {
  useGetSponsoredProducts,
  useGetSponsoredBrands,
  useSearchData,
  useSponsoredProductsPlacementConfig,
  useSponsoredBrandsPlacementConfig,
} from 'components/header/hooks';
import { State } from 'store/types';
import { getCustomerId } from 'selectors';
import type { ProductRedirectionStore, SuggestionsStore } from 'reducers';
import { createCookies, getLangFromCookies } from 'utils';
import { getRegionId, getRegionName } from './utils';

type SearchSponsoredData = {
  searchSuggestions: SuggestionsStore['suggestions'];
  fetchSuggestions: (value: string) => void;
  resetServices: () => void;
  productRedirection: ProductRedirectionStore['productRedirection'];
  resetSponsoredProducts: () => void;
  resetSponsoredBrands: () => void;
};

/**
 * @description обёртка над хуком useSearchData для Header
 * @returns { UseSearchBarLogicReturnType }
 */
export const useSearchSponsoredData = (): SearchSponsoredData => {
  const { dispatch, suggestions, cookies, model, productRedirection } = useStoreon<State>(
    'cookies',
    'suggestions',
    'productRedirection',
    'model',
  );

  const { fetchSuggestions, resetServices } = useSearchData(dispatch);
  const { getSponsoredProducts, resetSponsoredProducts } = useGetSponsoredProducts(dispatch);
  const sponsoredProductConfig = useSponsoredProductsPlacementConfig();
  const sponsoredBrandConfig = useSponsoredBrandsPlacementConfig();
  const { getSponsoredBrands, resetSponsoredBrands } = useGetSponsoredBrands(dispatch);

  const fetchSuggestionsSafe = useCallback(
    (value: string) => {
      const regionId = getRegionId(model?.properties);
      const regionName = getRegionName(regionId, model?.regions);
      const customerId = getCustomerId(cookies);
      const lang = getLangFromCookies(createCookies(cookies));

      if (regionId) {
        fetchSuggestions({
          query: value,
          region: regionId,
          customerId,
          lang,
        });
      }

      if (regionId && regionName && value) {
        if (sponsoredProductConfig) {
          const { session, link, placementId, placementLocationSRP } = sponsoredProductConfig;
          // запрос спонсорских товаров
          getSponsoredProducts({
            searchQuery: value,
            regionId,
            regionName,
            placementLocationSRP,
            ...(session && { session }),
            ...(!session && link && { 'link.name': link.name, 'link.value': link.value }),
            placementId,
          });
        }
        if (sponsoredBrandConfig) {
          const { session, link, placementId } = sponsoredBrandConfig;
          // запрос спонсорских брендов
          getSponsoredBrands({
            searchQuery: value,
            regionId,
            regionName,
            ...(session && { session }),
            ...(!session && link && { 'link.name': link.name, 'link.value': link.value }),
            placementId,
          });
        }
      }
    },
    [
      model?.properties,
      model?.regions,
      cookies,
      sponsoredProductConfig,
      sponsoredBrandConfig,
      fetchSuggestions,
      getSponsoredProducts,
      getSponsoredBrands,
    ],
  );

  return {
    searchSuggestions: suggestions,
    fetchSuggestions: fetchSuggestionsSafe,
    resetServices,
    productRedirection,
    resetSponsoredProducts,
    resetSponsoredBrands,
  };
};
